import React, { useEffect, ReactChild } from 'react'
import { useNavigate, Params, useParams } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import {
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Container
} from '@mui/material'
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps
} from 'react-router-dom'
import useTemplate from '../../hooks/useTemplate'
import CodeFormat from '../../components/CodeFormat/CodeFormat'
import DeleteTemplateModal from '../../components/DeleteTemplateModal/DeleteTemplateModal'

interface ListItemLinkProps {
  icon?: React.ReactElement
  to: string
}

function ListItemLink({ icon, to }: ListItemLinkProps) {
  const renderLink = React.useMemo(
    () =>
      React.forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'>>(
        function Link(itemProps, ref) {
          return (
            <RouterLink to={to} ref={ref} {...itemProps} role={undefined} />
          )
        }
      ),
    [to]
  )

  return (
    <ListItem button component={renderLink}>
      {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
      <ListItemText />
    </ListItem>
  )
}

const ContentBox = ({ children }: { children: ReactChild | ReactChild[] }) => {
  return (
    <Box sx={{ marginTop: '15px' }}>
      {children}
      <Divider />
    </Box>
  )
}

function TemplateDetail() {
  const navigate = useNavigate()
  const { id }: Params = useParams<string>()
  const { template, fetchTemplate, deleteTemplate } = useTemplate()

  useEffect(() => {
    id && fetchTemplate(id)
  }, [id])

  const handleDelete = async (): Promise<void> => {
    if (!id) {
      return
    }
    await deleteTemplate(id)
    navigate('/template')
  }

  return template ? (
    <Box sx={{ margin: '15px' }}>
      <Box sx={{ display: 'flex' }}>
        <ListItemLink to="/" icon={<ArrowBackIcon />} />
        <ListItemLink
          to={`/template/update/${template.id}`}
          icon={<EditIcon />}
        />
        <DeleteTemplateModal action={handleDelete}></DeleteTemplateModal>
      </Box>
      <ContentBox>
        <Typography variant="h2">{template.name}</Typography>
      </ContentBox>
      <ContentBox>
        <Typography variant="h5">Id: {template.id}</Typography>
      </ContentBox>
      <ContentBox>
        <Typography variant="h5">Content:</Typography>
        <CodeFormat>{template.content}</CodeFormat>
      </ContentBox>
      {template.header && (
        <ContentBox>
          <Typography variant="h4">Header:</Typography>
          <CodeFormat language="html">{template.header}</CodeFormat>
        </ContentBox>
      )}
      {template.header && template.headerHeight && (
        <ContentBox>
          <Typography variant="h4">Header height:</Typography>
          <Typography>{template.headerHeight} px</Typography>
        </ContentBox>
      )}
      {template.footer && (
        <ContentBox>
          <Typography variant="h4">Footer:</Typography>
          <CodeFormat>{template.footer}</CodeFormat>
        </ContentBox>
      )}
      {template.footer && template.footerHeight && (
        <ContentBox>
          <Typography variant="h4">Footer height:</Typography>
          <Typography>{template.footerHeight} px</Typography>
        </ContentBox>
      )}
    </Box>
  ) : (
    <></>
  )
}

export default TemplateDetail
